// src/components/LibraryChat.js
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faSpinner, faArrowLeft, faStop } from '@fortawesome/free-solid-svg-icons';
import { sendLibraryChatMessage } from '../api';
import styles from './LibraryChat.module.css';

const LibraryChat = ({ library, onBack }) => {
  const { t } = useTranslation();
  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatHistoryRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Scroll to bottom on new message
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [chatHistory]);

  useEffect(() => {
    const adjustTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = '24px';
        const scrollHeight = textarea.scrollHeight;
        textarea.style.height = `${Math.min(scrollHeight, 150)}px`;
      }
    };
    adjustTextareaHeight();
  }, [message]);

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = { sender: 'user', content: message };
    setChatHistory(prev => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);

    try {
      const historyForApi = chatHistory.map(msg => ({
        sender: msg.sender,
        content: msg.content,
      }));

      const response = await sendLibraryChatMessage(library.id, message, historyForApi);
      const botMessage = { sender: 'bot', content: response.response };
      setChatHistory(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = { sender: 'bot', content: t('errorSendingMessage') };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={styles.chatContainer}>
      <div className={styles.chatHeader}>
        <button onClick={onBack} className={styles.backButton}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h3 className={styles.chatTitle}>{t('chatWithLibrary', { name: library.name })}</h3>
      </div>
      <div className={styles.chatBody} ref={chatHistoryRef}>
        {chatHistory.map((msg, index) => (
          <div key={index} className={`${styles.messageWrapper} ${msg.sender === 'user' ? styles.userMessageWrapper : styles.botMessageWrapper}`}>
            <div className={`${styles.messageBubble} ${msg.sender === 'user' ? styles.userMessageBubble : styles.botMessageBubble}`}>
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className={`${styles.messageWrapper} ${styles.botMessageWrapper}`}>
            <div className={`${styles.messageBubble} ${styles.botMessageBubble}`}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          </div>
        )}
      </div>
      <div className={styles.inputAreaWrapper}>
        <div className={styles.inputArea}>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyUp={handleKeyUp}
            placeholder={t('askAboutYourDocs')}
            rows={1}
            disabled={isLoading}
          />
          <div className={styles.chatInputButtonsWrapper}>
            <button
              onClick={handleSendMessage}
              className={`${styles.chatIconButton} ${styles.sendButton}`}
              disabled={isLoading || !message.trim()}
              title={t('sendMessageHint')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
        <p className={styles.chatInfoText}>
          {t('libraryChatDisclaimer')}
        </p>
      </div>
    </div>
  );
};

export default LibraryChat;